import { useParams } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import ProjectLayout from '@/components/ProjectLayout'
import { projectService, type Project } from '@/services/projectService'
import { HttpError } from '@/lib/httpError'
import LoadingOverlay from '@/components/ui/LoadingOverlay'
import ErrorDisplay from '@/components/ui/ErrorDisplay'
import DeleteProjectModal, { type DeleteProjectModalRef } from '@/components/modals/DeleteProjectModal'

export default function ProjectInformationPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const { t, i18n } = useTranslation('projectInformation')
  const { refreshAuth, user } = useAuth()
  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const deleteModalRef = useRef<DeleteProjectModalRef>(null)

  // Permission checks for delete
  const isPlatformOwner = user?.platform_role === 'owner'
  const isProjectOwner = project?.role === 'owner'
  const isProjectAdmin = project?.role === 'admin'
  
  // Direct delete: platform owner or project owner
  const canDirectDelete = isPlatformOwner || isProjectOwner
  // Request delete: platform owner or project owner or project admin
  const canRequestDelete = isPlatformOwner || isProjectOwner || isProjectAdmin
  // Show delete button
  const canDelete = canDirectDelete || canRequestDelete

  const fetchProjectInfo = async () => {
    if (!projectId) return

    setLoading(true)
    setError('')

    try {
      const data = await projectService.getProjectById(projectId)
      setProject(data)
    } catch (err) {
      // Handle 401 errors with automatic token refresh
      if (err instanceof HttpError && err.status === 401) {
        try {
          await refreshAuth()
          // Retry the fetch after token refresh
          const data = await projectService.getProjectById(projectId)
          setProject(data)
        } catch (refreshError) {
          // If refresh fails, show error
          setError('Failed to load project information')
        } finally {
          setLoading(false)
        }
        return
      }

      const errorMessage = err instanceof Error ? err.message : 'Failed to load project information'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProjectInfo()
  }, [projectId])

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      const locale = i18n.language === 'zh' ? 'zh-TW' : 'en-US'

      return date.toLocaleString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
    } catch {
      return dateString
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200'
      case 'archived':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getRoleColor = (role?: string) => {
    switch (role?.toLowerCase()) {
      case 'owner':
        return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'admin':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'member':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'viewer':
        return 'bg-gray-100 text-gray-800 border-gray-200'
      case 'observer':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  if (loading) {
    return (
      <ProjectLayout>
        <LoadingOverlay message={t('loading', 'Loading project information...')} fullScreen />
      </ProjectLayout>
    )
  }

  if (error) {
    return (
      <ProjectLayout>
        <ErrorDisplay
          error={error}
          title={t('errors.title', 'Error')}
          onRefresh={fetchProjectInfo}
          fullScreen
        />
      </ProjectLayout>
    )
  }

  if (!project) {
    return (
      <ProjectLayout>
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-gray-500">{t('notFound', 'Project not found')}</p>
          </div>
        </div>
      </ProjectLayout>
    )
  }

  const handleOpenDeleteModal = () => {
    if (project) {
      deleteModalRef.current?.open(project)
    }
  }

  return (
    <ProjectLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-text-dark">{t('title', 'Project Information')}</h1>
            <p className="text-gray-600 mt-2">{t('description', 'View and manage project details')}</p>
          </div>
          {canDelete && (
            <button
              onClick={handleOpenDeleteModal}
              className="flex items-center gap-2 px-4 py-2 ml-4 mt-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span>{t('deleteProject', '刪除專案')}</span>
            </button>
          )}
        </div>

        <div className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{t('sections.basic', 'Basic Information')}</h2>
            </div>
            <div className="p-6">
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.name', 'Project Name')}</dt>
                  <dd className="text-base text-gray-900">{project.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.id', 'Project ID')}</dt>
                  <dd className="text-base text-gray-900 font-mono text-sm">{project.id}</dd>
                </div>
                <div className="md:col-span-2">
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.description', 'Description')}</dt>
                  <dd className="text-base text-gray-900">{project.description || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.status', 'Status')}</dt>
                  <dd>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(project.status)}`}>
                      {t(`status.${project.status}`, project.status)}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.yourRole', 'Your Role')}</dt>
                  <dd>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getRoleColor(project.role)}`}>
                      {t(`roles.${project.role}`, project.role || '-')}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Owner Information */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{t('sections.owner', 'Owner Information')}</h2>
            </div>
            <div className="p-6">
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.ownerName', 'Owner Name')}</dt>
                  <dd className="text-base text-gray-900">{project.owner.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.ownerAccount', 'Owner Account')}</dt>
                  <dd className="text-base text-gray-900">{project.owner.account}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.ownerType', 'Account Type')}</dt>
                  <dd className="text-base text-gray-900 capitalize">{project.owner.type}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.platformRole', 'Platform Role')}</dt>
                  <dd className="text-base text-gray-900 capitalize">{project.owner.platform_role}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Timestamps */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{t('sections.timestamps', 'Timestamps')}</h2>
            </div>
            <div className="p-6">
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.createdAt', 'Created At')}</dt>
                  <dd className="text-base text-gray-900">{formatDate(project.created_at)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">{t('fields.updatedAt', 'Updated At')}</dt>
                  <dd className="text-base text-gray-900">{formatDate(project.updated_at)}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Project Modal */}
      <DeleteProjectModal
        ref={deleteModalRef}
        canDirectDelete={canDirectDelete}
        canRequestDelete={canRequestDelete}
      />
    </ProjectLayout>
  )
}
